import { createContext, ReactNode, useEffect, useRef, useState } from 'react';

import { getFeaturesForPlan, PlanTier } from '@repo/contracts/common';

import { useGetTenantSubscriptionQuery } from '@/features/entitlements/api/subscription-api';
import { useAuth } from '@/lib/auth';
import { updateGlobalScope } from '@/redux/api/tenant-api';

export type PricingPlan = {
  name: PlanTier;
  features: string[];
};

export type CurrentTenant = {
  name: string;
  slug: string;
  pricingPlan: PricingPlan;
  availableCountries: string[];
};

type CurrentTenantContextType = {
  currentTenant: CurrentTenant | null;
  setTenant: (tenant: CurrentTenant) => void;
  clearTenant: () => void;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => void;
};

export const CurrentTenantContext = createContext<
  CurrentTenantContextType | undefined
>(undefined);

export const CurrentTenantProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { isAuthenticated, isLoading, tenantSlug, organizationSlug } =
    useAuth();
  const [currentTenant, setCurrentTenant] = useState<CurrentTenant | null>(
    null,
  );
  const prevScopeRef = useRef<string | null>(null);

  // Fetch subscription from account-api
  const {
    data: subscription,
    isLoading: subscriptionLoading,
    isError: subscriptionError,
    error: tenantError,
    refetch: refetchSubscription,
  } = useGetTenantSubscriptionQuery(tenantSlug || '', {
    skip: !isAuthenticated || !tenantSlug,
  });

  // CRITICAL: Update scope SYNCHRONOUSLY during render, not in useEffect.
  // This ensures RTK Query has the correct scope headers before
  // CurrentUserProvider (nested below) fires useGetCurrentUserQuery.
  // Same pattern as ScopeHeadersProvider.
  if (isAuthenticated && tenantSlug) {
    const scopeKey = `${tenantSlug}:${organizationSlug}`;
    if (prevScopeRef.current !== scopeKey) {
      prevScopeRef.current = scopeKey;
      updateGlobalScope(tenantSlug, organizationSlug ?? undefined);
    }
  }

  useEffect(() => {
    if (isLoading || subscriptionLoading) return;

    if (isAuthenticated && tenantSlug && subscription) {
      const tenant: CurrentTenant = {
        name: tenantSlug,
        slug: tenantSlug,
        pricingPlan: {
          name: subscription.planTier as PlanTier,
          features: getFeaturesForPlan(subscription.planTier as PlanTier),
        },
        availableCountries: subscription.availableCountries,
      };
      setCurrentTenant(tenant);
    } else if (!isLoading && !isAuthenticated) {
      setCurrentTenant(null);
    }
  }, [
    isAuthenticated,
    isLoading,
    tenantSlug,
    subscription,
    subscriptionLoading,
  ]);

  const setTenant = (tenant: CurrentTenant) => {
    setCurrentTenant(tenant);
  };

  const clearTenant = () => {
    setCurrentTenant(null);
  };

  const contextIsLoading = isLoading || subscriptionLoading;

  return (
    <CurrentTenantContext.Provider
      value={{
        currentTenant,
        setTenant,
        clearTenant,
        isLoading: contextIsLoading,
        isError: subscriptionError,
        error: tenantError,
        refetch: refetchSubscription,
      }}
    >
      {children}
    </CurrentTenantContext.Provider>
  );
};
